import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import WeekItem from "./WeekItem";
import {
	applyCopiedScheduleForSelectedWeek,
	getWeekItemsWithShiftData,
	getSwedishWeekDayName,
	getWeekItems,
	isValidTime,
	getISOWeekString,
} from "@utils/dateUtils";
import type { ScheduleWeekItem, ShiftItem, StepTwoData } from "@models/Order";
import {
	useDisclosure,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	DrawerHeader,
	DrawerBody,
	HStack,
	Button,
	DrawerFooter,
	Text,
	Box,
	Divider,
	VStack,
} from "@chakra-ui/react";
import EmptyWhiteCircle from "@components/icons/EmptyWhiteCircle";
import CheckCircleIcon from "@components/icons/CheckCircleIcon";
import moment from "moment";

interface Props {
	data: StepTwoData;
	onChangeData: (data: StepTwoData) => void;
	toggleNextButtonDisable: (status: boolean) => void;
}

const StepTwo = ({ data, toggleNextButtonDisable, onChangeData }: Props, ref: any) => {
	const [weekList, setWeekList] = useState<ScheduleWeekItem[]>([]);
	const [scheduleCopiedWeekNumberList, setScheduleCopiedWeekNumberList] = useState<string[]>([]);
	const [copiedShiftItems, setCopiedShiftItems] = useState<ShiftItem[]>([]);

	const {
		isOpen: isOpenCopyWeekDrawer,
		onOpen: onOpenCopyWeekDrawer,
		onClose: onCloseCopyWeekDrawer,
	} = useDisclosure();

	useEffect(() => {
		if (data.shifts.length > 0) {
			setWeekList(getWeekItemsWithShiftData(data.shifts, 26, undefined, true));
		} else {
			setWeekList(getWeekItems(26));
		}
	}, [data]);

	useImperativeHandle(
		ref,
		() => ({
			onConfirmData() {
				onChangeData({
					shifts: weekList
						.flatMap((weekItem) => weekItem.dateList)
						.filter((item) => isValidTime(item.start) && isValidTime(item.end)),
				});
			},
		}),
		[weekList],
	);

	useEffect(() => {
		const weekListData = weekList.flatMap((weekItem) => weekItem.dateList);
		if (!!weekListData.find((item) => isValidTime(item.start) && isValidTime(item.start))) {
			toggleNextButtonDisable(false);
		} else {
			toggleNextButtonDisable(true);
		}
	}, [weekList, toggleNextButtonDisable]);

	const isCopyWeekButtonDisabled = scheduleCopiedWeekNumberList.length === 0;

	const handleChangeSchedule = useCallback(
		(dateItems: ShiftItem[], weekNumber: string) => {
			setWeekList((prev) => {
				const weekListData = [...prev];

				const toUpdateWeekItemIndex = prev.findIndex((wi) => wi.weekNumber === weekNumber);

				weekListData.splice(toUpdateWeekItemIndex, 1, {
					dateList: dateItems,
					weekNumber,
				});

				return weekListData;
			});
		},
		[],
	);

	const handleCopyWeekButtonClick = useCallback(
		(copiedDateItems: ShiftItem[]) => {
			setCopiedShiftItems(copiedDateItems.filter((di) => isValidTime(di.start) && isValidTime(di.end)));
			onOpenCopyWeekDrawer();
		},
		[onOpenCopyWeekDrawer],
	);

	const handleConfirmCopyWeekSchedule = useCallback(() => {
		setWeekList((prev) => {
			const updatedWeekList = [...prev];
			scheduleCopiedWeekNumberList.forEach((copiedWeekNumber) => {
				const weekItemIndex = updatedWeekList.findIndex((wi) => wi.weekNumber === copiedWeekNumber);

				copiedShiftItems.forEach((copiedScheduleItem) => {
					const dateIndex = updatedWeekList[weekItemIndex].dateList.findIndex(
						(di) => getSwedishWeekDayName(di.start) === getSwedishWeekDayName(copiedScheduleItem.start),
					);

					if (dateIndex >= 0) {
						updatedWeekList[weekItemIndex].dateList.splice(
							dateIndex,
							1,
							applyCopiedScheduleForSelectedWeek(copiedWeekNumber, copiedScheduleItem),
						);
					}
				});
			});
			return updatedWeekList;
		});
		onCloseCopyWeekDrawer();
	}, [scheduleCopiedWeekNumberList, copiedShiftItems, onCloseCopyWeekDrawer]);

	const weekItemClickHandler = (weekNumber: string) => {
		setScheduleCopiedWeekNumberList((prev) => {
			if (prev.includes(weekNumber)) return prev.filter((wn) => wn !== weekNumber);
			return [...prev, weekNumber];
		});
	};

	return (
		<>
			<VStack alignItems="start" mb={8} spacing="3">
				<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
					När behöver du hjälp?
				</Text>
				<Divider />
			</VStack>

			{weekList.map((weekItem) => (
				<WeekItem
					key={weekItem.weekNumber}
					dateList={weekItem.dateList}
					onChangeDateList={handleChangeSchedule}
					onCopyWeek={handleCopyWeekButtonClick}
				/>
			))}
			<Drawer
				placement={"right"}
				onClose={() => {
					onCloseCopyWeekDrawer();
					setScheduleCopiedWeekNumberList([]);
				}}
				isOpen={isOpenCopyWeekDrawer}
			>
				<DrawerOverlay />
				<DrawerContent borderLeftRadius={"25px"}>
					<DrawerCloseButton />
					<DrawerHeader borderBottomWidth="1px" textAlign={"center"}>
						<Text>Kopiera vecka</Text>
						<Text fontWeight={400} fontSize={13} color={"#999999"}>
							Du kan kopiera pass för vald vecka till en eller flera andra veckor.
						</Text>
					</DrawerHeader>
					<DrawerBody overflowX={"hidden"}>
						{weekList.map((wi) => {
							const isSourceWeekNumber =
								copiedShiftItems[0]?.start &&
								getISOWeekString(moment(copiedShiftItems[0].start).toDate()) === wi.weekNumber;
							return (
								<Box
									key={wi.weekNumber}
									w="100%"
									my={"auto"}
									p={3}
									m={2}
									borderRadius={"18px"}
									border={"1px solid #E6E6E6"}
								>
									<HStack
										alignItems={"center"}
										cursor={isSourceWeekNumber ? "not-allowed" : "pointer"}
										onClick={isSourceWeekNumber ? undefined : () => weekItemClickHandler(wi.weekNumber)}
									>
										{scheduleCopiedWeekNumberList.includes(wi.weekNumber) || isSourceWeekNumber ? (
											<CheckCircleIcon fillColor={isSourceWeekNumber ? "#9EA0A6" : ""} />
										) : (
											<EmptyWhiteCircle />
										)}
										<Text fontWeight={500} fontSize={14} my={1} color={"#999999"}>
											{`Vecka ${wi.weekNumber.split("W")[1]}`}
										</Text>
									</HStack>
								</Box>
							);
						})}
					</DrawerBody>
					<Button
						variant="solid"
						height="40px"
						borderRadius={"20px"}
						color="white"
						bg={"#2A2A2A"}
						mx={"auto"}
						_hover={{
							backgroundColor: isCopyWeekButtonDisabled ? "#9fa0a3" : "#2A2A2A",
						}}
						onClick={handleConfirmCopyWeekSchedule}
						isDisabled={isCopyWeekButtonDisabled}
					>
						Kopiera skift till valda veckor
					</Button>
					<DrawerFooter></DrawerFooter>
				</DrawerContent>
			</Drawer>
		</>
	);
};

export default forwardRef(StepTwo);
